import { useState, useEffect, useCallback, useMemo } from "react"
import { listIntegrationConnectionsApi } from "@/api/integrations"
import type { NylasConnection } from "@equaliq/api-model"

interface UseIntegrationConnectionsOptions {
  /** When false, skips the API call (e.g. in demo mode). Defaults to true. */
  enabled?: boolean
}

/**
 * Case-insensitive scope substring match. Microsoft Graph scopes are
 * mixed-case URIs like `https://graph.microsoft.com/Calendars.ReadWrite`
 * and `Mail.ReadBasic`, so a naive `s.includes("calendar")` misses them.
 * Google scopes are lowercase already; this is safe for both providers.
 */
function scopeContains(scope: string, needle: string): boolean {
  return scope.toLowerCase().includes(needle.toLowerCase())
}

/**
 * Hook to manage Nylas integration connections.
 * Loads connections and provides utilities for mapping connections to integrations.
 */
export function useIntegrationConnections(
  options: UseIntegrationConnectionsOptions = {}
) {
  const { enabled = true } = options
  const [connections, setConnections] = useState<NylasConnection[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<Error | null>(null)

  const loadConnections = useCallback(async () => {
    if (!enabled) return
    try {
      setLoading(true)
      setError(null)
      const result = await listIntegrationConnectionsApi()
      setConnections(result.connections || [])
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load connections"))
      console.error("Failed to load connections:", err)
    } finally {
      setLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (enabled) {
      loadConnections()
    }
  }, [enabled, loadConnections])

  // Map connections to integration IDs
  const connectedIntegrationIds = useMemo(() => {
    const ids = new Set<string>()
    connections.forEach((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      // Google integrations
      if (provider === "google") {
        if (scopes.some((s) => scopeContains(s, "gmail") || scopeContains(s, "mail"))) {
          ids.add("gmail")
        }
        if (scopes.some((s) => scopeContains(s, "calendar"))) {
          ids.add("google-calendar")
        }
      }

      // Microsoft/Outlook integrations
      if (provider === "microsoft" || provider === "outlook") {
        if (scopes.some((s) => scopeContains(s, "mail"))) {
          ids.add("outlook-email")
        }
        if (scopes.some((s) => scopeContains(s, "calendar"))) {
          ids.add("outlook-calendar")
        }
      }
    })
    return ids
  }, [connections])

  // Map integration IDs to connections (first connection only)
  const integrationConnectionMap = useMemo(() => {
    const map = new Map<string, NylasConnection>()
    connections.forEach((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      const integrationIds: string[] = []

      // Google integrations
      if (provider === "google") {
        if (scopes.some((s) => scopeContains(s, "gmail") || scopeContains(s, "mail"))) {
          integrationIds.push("gmail")
        }
        if (scopes.some((s) => scopeContains(s, "calendar"))) {
          integrationIds.push("google-calendar")
        }
      }

      // Microsoft/Outlook integrations
      if (provider === "microsoft" || provider === "outlook") {
        if (scopes.some((s) => scopeContains(s, "mail"))) {
          integrationIds.push("outlook-email")
        }
        if (scopes.some((s) => scopeContains(s, "calendar"))) {
          integrationIds.push("outlook-calendar")
        }
      }

      integrationIds.forEach((id) => {
        if (!map.has(id)) {
          map.set(id, conn)
        }
      })
    })
    return map
  }, [connections])

  // Get all connections for a specific integration
  const getConnectionsForIntegration = useCallback((integrationId: string): NylasConnection[] => {
    return connections.filter((conn) => {
      const provider = conn.provider?.toLowerCase() || ""
      const scopes = conn.scopes || []

      // Google integrations
      if (integrationId === "gmail") {
        return provider === "google" && scopes.some((s) => scopeContains(s, "gmail") || scopeContains(s, "mail"))
      }
      if (integrationId === "google-calendar") {
        return provider === "google" && scopes.some((s) => scopeContains(s, "calendar"))
      }

      // Microsoft/Outlook integrations
      if (integrationId === "outlook-email") {
        return (provider === "microsoft" || provider === "outlook") && scopes.some((s) => scopeContains(s, "mail"))
      }
      if (integrationId === "outlook-calendar") {
        return (provider === "microsoft" || provider === "outlook") && scopes.some((s) => scopeContains(s, "calendar"))
      }

      return false
    })
  }, [connections])

  return {
    connections,
    loading,
    error,
    connectedIntegrationIds,
    integrationConnectionMap,
    getConnectionsForIntegration,
    refetch: loadConnections,
  }
}
